import { useState, useEffect } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import * as AppPics from 'assets/images';
import { AiOutlineEye } from 'react-icons/ai';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import SelectBoxV2 from 'components/Inputs/SelectBoxV2';
import TablePaginate from 'components/Paginate';
import usePodcastSubscriptions from 'hooks/API/GET/usePodcastSubscriptions';
import useDebounce from 'hooks/useDebounce';

const STATUS_BADGE = {
  active: 'badge bg-success',
  pending: 'badge bg-warning',
  cancelled: 'badge bg-secondary',
  expired: 'badge bg-danger',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired', label: 'Expired' },
];

const formatDate = (iso) =>
  iso && new Date(iso).getFullYear() > 2000
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

function parseQuery(search) {
  const p = new URLSearchParams(search);
  return {
    status: p.get('status') || '',
    search: p.get('search') || '',
    page: Number(p.get('page')) || 0,
  };
}

function buildQuery(filters) {
  const p = new URLSearchParams();
  if (filters.status) p.set('status', filters.status);
  if (filters.search) p.set('search', filters.search);
  if (filters.page > 0) p.set('page', filters.page);
  return p.toString();
}

function PodcastSubscriptionList() {
  const history = useHistory();
  const location = useLocation();

  const [filters, setFilters] = useState(() => parseQuery(location.search));
  const [searchTerm, setSearchTerm] = useState(filters.search);
  const [enableFilter, setEnableFilter] = useState(!!filters.status);

  const debouncedSearch = useDebounce(searchTerm, 600);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, search: debouncedSearch, page: 0 }));
  }, [debouncedSearch]);

  const subscriptionsQuery = usePodcastSubscriptions({ enabled: true, params: filters });
  const body = subscriptionsQuery.data?.data?.data || {};
  const subscriptions = body.result || [];
  const meta = body.meta || { total: 0, limit: 40 };

  useEffect(() => {
    const qs = buildQuery(filters);
    history.replace({ search: qs ? `?${qs}` : '' });
  }, [filters]);

  const applyFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value, page: 0 }));

  const clearAllFilters = () => {
    setFilters({ status: '', search: '', page: 0 });
    setSearchTerm('');
    setEnableFilter(false);
    history.replace(location.pathname);
  };

  const isLoading = subscriptionsQuery.isLoading;

  return (
    <div className="mainWraper coursetabsection">
      <div className="tab">
        <ul className="tab__menu">
          <li className="back">
            <button className="btn-back" onClick={() => history.push('/podcast')}>
              <img src={AppPics.BackSvg} alt="Back" />
            </button>
          </li>
        </ul>

        <div className="tab__content ___adjust___tab___padding">
          <div className="tab__content-actHead">
            <AppBreadcrumbs
              links={[{ label: 'Podcast', to: '/podcast' }]}
              current="Subscriptions"
            />
          </div>

          <div className="tab__content-actBody">
            <div className="px-5 py-5">
              <div className="tab__contentMenu">
                <ul className="tab__contentMenu-list">
                  <li>
                    <h2 className="prime-head p-0 boldtitle text-nowrap">Subscriptions</h2>
                  </li>
                </ul>

                <div className="mainHeader__search">
                  <ul className="tab__contentMenu-list">
                    <li className="relative">
                      <input
                        className="mainHeader__searchInput"
                        placeholder="Search name, email, mobile..."
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <span className="mainHeader__searchBtn">
                        <img src={AppPics.TopMenuSearchSvg} alt="Search" />
                      </span>
                    </li>
                    <li className="px-4 flex items-center">
                      <button
                        type="button"
                        onClick={() => setEnableFilter(!enableFilter)}
                        className="flex items-center gap-1"
                      >
                        <img src={AppPics.TabFilterSvg} alt="Filter" />
                        <span className="viewsettingtitle">Filter</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              {enableFilter && (
                <div className="row mt-3 mb-4">
                  <div className="col-lg-3">
                    <SelectBoxV2
                      labelProps={{ label: 'Status' }}
                      selectProps={{
                        options: STATUS_OPTIONS,
                        value: STATUS_OPTIONS.find((o) => o.value === filters.status) ?? null,
                        getOptionLabel: (opt) => opt.label,
                        getOptionValue: (opt) => opt.value,
                        onChange: (opt) => applyFilter('status', opt.value),
                      }}
                    />
                  </div>

                  <div className="col-lg-3 d-flex">
                    <button
                      type="button"
                      className="btn btn-plane p--tint"
                      style={{ alignSelf: 'end' }}
                      onClick={clearAllFilters}
                    >
                      Clear filter
                    </button>
                  </div>
                </div>
              )}

              <div className="tab__contentBody">
                <div className="tableWrap">
                  <div className="table-responsive">
                    <table className="table tableWrap__table">
                      <thead className="thead-light __appTheadColor">
                        <tr>
                          <th className="text-center">#</th>
                          <th className="text-center">User</th>
                          <th className="text-center">Plan</th>
                          <th className="text-center">Status</th>
                          <th className="text-center">Started</th>
                          <th className="text-center">Access Until</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan={7} className="text-center py-5">
                              <span className="spinner-border spinner-border-sm me-2" />
                              Loading…
                            </td>
                          </tr>
                        ) : subscriptions.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-5 text-muted">
                              No subscriptions found
                            </td>
                          </tr>
                        ) : (
                          subscriptions.map((item, idx) => (
                            <tr
                              key={item._id}
                              style={{ cursor: 'pointer' }}
                              onClick={() => history.push(`/podcast-subscriptions/${item._id}`)}
                            >
                              <td className="text-center">
                                {filters.page * (meta.limit || 40) + idx + 1}
                              </td>
                              <td>
                                <div className="fw-semibold">{item.user?.name || '—'}</div>
                                <small className="text-muted">
                                  {item.user?.email || item.user?.mobile || ''}
                                </small>
                              </td>
                              <td className="text-center" style={{ textTransform: 'capitalize' }}>
                                {item.plan}
                              </td>
                              <td className="text-center">
                                <span
                                  className={STATUS_BADGE[item.status] || 'badge bg-secondary'}
                                  style={{ color: '#fff' }}
                                >
                                  {item.status}
                                </span>
                              </td>
                              <td className="text-center">{formatDate(item.createdAt)}</td>
                              <td className="text-center">{formatDate(item.currentPeriodEnd)}</td>
                              <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                <Link
                                  to={`/podcast-subscriptions/${item._id}`}
                                  className="actions-icons mx-2"
                                  title="View Details"
                                >
                                  <AiOutlineEye style={{ color: '#7e1f8c', fontSize: '24px' }} />
                                </Link>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {!isLoading && meta.total > (meta.limit || 40) && (
                  <TablePaginate
                    currentPage={filters.page + 1}
                    maxCount={meta.total}
                    defaultLimit={meta.limit || 40}
                    rows={[10, 20, 40, 60, 100]}
                    setPage={(page) => setFilters((prev) => ({ ...prev, page }))}
                    handleChange={(limit) =>
                      setFilters((prev) => ({ ...prev, limit: Number(limit), page: 0 }))
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PodcastSubscriptionList;
